import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import showToast from '../../components/Toast';
import { FiMail, FiLock, FiUser, FiEye, FiEyeOff, FiArrowRight, FiGithub, FiShield, FiClock, FiRefreshCw, FiEdit3 } from 'react-icons/fi';
import { loginValidationSchema, registerValidationSchema } from '../../validation/authValidation';
import AxiosInstance, { resetLogoutFlag } from '../../AxiosInstance';
import config from '../../config/frontend.config';
import './Auth.css';

const API_BASE_URL = config.api.baseURL + '/' + config.api.apiPath;

// Global flag to prevent duplicate login attempts
let globalLoginInProgress = false;

function Auth() {
  const navigate = useNavigate();
  
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // OTP Login states
  const [loginMode, setLoginMode] = useState('password'); // 'password' or 'otp'
  const [otpSent, setOtpSent] = useState(false);
  const [otpEmail, setOtpEmail] = useState('');
  const [otpCountdown, setOtpCountdown] = useState(0);
  const [resendCountdown, setResendCountdown] = useState(0);

  useEffect(() => {
    // Reset logout flag when component mounts
    resetLogoutFlag();
    
    // Check if user is already logged in
    const token = config.getAuthToken();
    if (token) {
      navigate('/');
    }
  }, [navigate]);

  // Countdown timer for OTP expiration
  useEffect(() => {
    let interval;
    if (otpCountdown > 0) {
      interval = setInterval(() => {
        setOtpCountdown(prev => {
          if (prev <= 1) {
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [otpCountdown]);

  // Countdown timer for resend cooldown
  useEffect(() => {
    let interval;
    if (resendCountdown > 0) {
      interval = setInterval(() => {
        setResendCountdown(prev => {
          if (prev <= 1) {
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendCountdown]);

  // Send OTP function
  const sendOTP = async (email) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await AxiosInstance.post('/otp/send-otp', {
        EmailAddress: email
      });
      
      
      if (response.data.statusCode === 200) {
        setOtpSent(true);
        setOtpEmail(email);
        // Use actual API response values for timers
        const otpExpiresIn = response.data.otpExpiresIn || 600;
        const resendAfter = response.data.resendAfter || 60;
        
        
        setOtpCountdown(otpExpiresIn);
        setResendCountdown(resendAfter);
        
        // Force timer to start by setting state again after a small delay
        setTimeout(() => {
          setOtpCountdown(otpExpiresIn);
          setResendCountdown(resendAfter);
        }, 100);
        
        showToast.success('OTP sent successfully! Check your email.');
      } else {
        showToast.error(response.data.message || 'Failed to send OTP');
      }
    } catch (error) {
      console.error('💥 Send OTP error:', error);
      console.error('Error response:', error.response?.data);
      
      // Handle 429 rate limit error - extract timer values from error response
      if (error.response?.status === 429) {
        const errorData = error.response?.data;
        
        // Extract timer values from error response
        const resendAfter = errorData?.resendAfter || 60;
        const otpExpiresIn = errorData?.otpExpiresIn || 0;
        
        
        // Set both timers from error response
        setResendCountdown(resendAfter);
        if (otpExpiresIn > 0) {
          setOtpCountdown(otpExpiresIn);
          // Set otpSent to true so timer display shows
          setOtpSent(true);
          setOtpEmail(email);
        }
        
        // Force timer to start by setting state again after a small delay
        setTimeout(() => {
          setResendCountdown(resendAfter);
          if (otpExpiresIn > 0) {
            setOtpCountdown(otpExpiresIn);
          }
        }, 100);
        
        // Show info message that timer is running
        showToast.warning(`Please wait ${resendAfter} seconds before requesting a new OTP.`);
        
        // Don't show error message for 429 - let the timer handle it
        return;
      }
      
      const errorMessage = error.response?.data?.message || 'Failed to send OTP';
      showToast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP function - uses same API as send OTP
  const resendOTP = async () => {
    if (resendCountdown > 0) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await AxiosInstance.post('/otp/send-otp', {
        EmailAddress: otpEmail
      });
      
      if (response.data.statusCode === 200) {
        // Use actual API response values for timers
        const otpExpiresIn = response.data.otpExpiresIn || 600;
        const resendAfter = response.data.resendAfter || 60;
        
        
        setOtpCountdown(otpExpiresIn);
        setResendCountdown(resendAfter);
        
        // Force timer to start by setting state again after a small delay
        setTimeout(() => {
          setOtpCountdown(otpExpiresIn);
          setResendCountdown(resendAfter);
        }, 100);
      } else {
        showToast.error(response.data.message || 'Failed to resend OTP');
      }
    } catch (error) {
      console.error('Resend OTP error:', error);
      
      // Handle 429 rate limit error - extract timer values from error response
      if (error.response?.status === 429) {
        const errorData = error.response?.data;
        
        // Extract timer values from error response
        const resendAfter = errorData?.resendAfter || 60;
        const otpExpiresIn = errorData?.otpExpiresIn || 0;
        
        
        // Set both timers from error response
        setResendCountdown(resendAfter);
        if (otpExpiresIn > 0) {
          setOtpCountdown(otpExpiresIn);
        }
        
        // Force timer to start by setting state again after a small delay
        setTimeout(() => {
          setResendCountdown(resendAfter);
          if (otpExpiresIn > 0) {
            setOtpCountdown(otpExpiresIn);
          }
        }, 100);
        
        // Show success message that timer is running
        setSuccess(`Timer is running. Please wait ${resendAfter} seconds before requesting a new OTP.`);
        
        // Don't show error message for 429 - let the timer handle it
        return;
      }
      
      const errorMessage = error.response?.data?.message || 'Failed to resend OTP';
      showToast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    
    // Global check to prevent duplicate login attempts
    if (globalLoginInProgress) {
      setSubmitting(false);
      return;
    }
    
    try {
      globalLoginInProgress = true;
      setLoading(true);
      setError(null);
      setSuccess(null);
      if (isLogin) {
        if (loginMode === 'otp' && !otpSent) {
          // Send OTP first
          await sendOTP(values.email);
          return;
        } else if (loginMode === 'otp' && otpSent) {
          // Verify OTP and login
          const response = await AxiosInstance.post(`/user/login`, {
            EmailAddress: values.email,
            otpLogin: true,
            otp: values.otp
          });
          
          if (response.data.statusCode === 200) {
            // Reset logout flag on successful login
            resetLogoutFlag();
            
            // Store token and user data
            config.setAuthToken(response.data.token);
            config.setUserId(response.data.user.UserId);
            
            // Store role information
            config.setUserRole(response.data.user.Role || 'member');
            config.setUserData(response.data.user);
            
            // Show appropriate success message
            const isSuperAdmin = response.data.user.isSuperAdmin;
            showToast.success(isSuperAdmin ? 'Super Admin login successful!' : 'Login successful!');
            
            // Dispatch custom event to notify App component
            window.dispatchEvent(new Event('loginSuccess'));
            
            // Small delay to ensure state is updated
            setTimeout(() => {
              navigate('/');
            }, 100);
          } else {
            showToast.error(response.data.message || 'OTP verification failed');
            if (response.data.attemptsRemaining !== undefined) {
              showToast.warning(`${response.data.attemptsRemaining} attempts remaining`);
            }
          }
        } else {
        // Regular password login
        const response = await AxiosInstance.post(`/user/login`, {
          EmailAddress: values.email,
          Password: values.password
        });
        
        if (response.data.statusCode === 200) {
          // Reset logout flag on successful login
          resetLogoutFlag();
          
          // Store token and user data
          config.setAuthToken(response.data.token);
          config.setUserId(response.data.user.UserId);
          
          // Store role information
          config.setUserRole(response.data.user.Role || 'member');
          config.setUserData(response.data.user);
          
          // Show appropriate success message
          const isSuperAdmin = response.data.user.isSuperAdmin;
          showToast.success(isSuperAdmin ? 'Super Admin login successful!' : 'Login successful!');
          
          // Dispatch custom event to notify App component
          window.dispatchEvent(new Event('loginSuccess'));
          
          // Small delay to ensure state is updated
          setTimeout(() => {
            navigate('/');
          }, 100);
        } else {
          showToast.error(response.data.message || 'Login failed');
          if (response.data.attemptsRemaining !== undefined) {
            showToast.warning(`${response.data.attemptsRemaining} attempts remaining`);
            }
          }
        }
      } else {
        // Register API call
        const response = await AxiosInstance.post(`/user/create`, {
          FirstName: values.firstName,
          LastName: values.lastName,
          CompanyName: values.companyName,
          EmailAddress: values.email,
          Password: values.password,
          ConfirmPassword: values.confirmPassword
        });
        
        if (response.data.statusCode === 200) {
          showToast.success('Registration successful! Please login.');
          setIsLogin(true);
          resetForm();
        } else {
          showToast.error(response.data.message || 'Registration failed');
        }
      }
    } catch (error) {
      console.error('Auth error:', error);
      
      // Handle specific login errors - only show ONE toast per error
      if (isLogin && error.response?.status === 401) {
        const errorMessage = error.response?.data?.message || 'Invalid email or password. Please try again.';
        showToast.error(errorMessage);
      } else if (isLogin && error.response?.status === 423) {
        // Account locked
        const errorMessage = error.response?.data?.message || 'Account is locked';
        showToast.error(errorMessage);
      } else {
        const errorMessage = error.response?.data?.message || (isLogin ? 'Login failed' : 'Registration failed');
        showToast.error(errorMessage);
      }
    } finally {
      setLoading(false);
      setSubmitting(false);
      globalLoginInProgress = false; // Reset global flag
    }
  };

  const handleGoogleLogin = () => {
    // Redirect to Google OAuth endpoint
    const googleAuthUrl = `${API_BASE_URL}/auth/google`;
    window.location.href = googleAuthUrl;
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setError(null);
    setSuccess(null);
    // Reset OTP states when switching between login/register
    setLoginMode('password');
    setOtpSent(false);
    setOtpEmail('');
    setOtpCountdown(0);
    setResendCountdown(0);
  };

  const toggleLoginMode = () => {
    const newMode = loginMode === 'password' ? 'otp' : 'password';
    setLoginMode(newMode);
    setError(null);
    setSuccess(null);
    // Reset OTP states when switching login modes
    setOtpSent(false);
    setOtpEmail('');
    setOtpCountdown(0);
    setResendCountdown(0);
  };

  const resetOTPFlow = () => {
    setOtpSent(false);
    setOtpEmail('');
    setOtpCountdown(0);
    setResendCountdown(0);
    setError(null);
  };

  const getInitialValues = () => {
    if (isLogin) {
      return {
        email: otpEmail || '',
        password: '',
        otp: ''
      };
    } else {
      return {
        firstName: '',
        lastName: '',
        companyName: '',
        email: '',
        password: '',
        confirmPassword: ''
      };
    }
  };

  const getValidationSchema = () => {
    return isLogin ? loginValidationSchema : registerValidationSchema;
  };

  return (
    <div className="auth-container">
      <div className="auth-background">
        <div className="auth-background-overlay"></div>
        <div className="auth-background-content">
          <h1>Welcome to MailMaster</h1>
          <p>Create stunning email campaigns that convert. Join thousands of businesses growing with our platform.</p>
          <div className="auth-features">
            <div className="feature">
              <div className="feature-icon">📧</div>
              <span>Drag & Drop Editor</span>
            </div>
            <div className="feature">
              <div className="feature-icon">📊</div>
              <span>Advanced Analytics</span>
            </div>
            <div className="feature">
              <div className="feature-icon">🎯</div>
              <span>Smart Automation</span>
            </div>
          </div>
        </div>
      </div>

      <div className="auth-form-container">
        <div className="auth-form-wrapper">
          <div className="auth-header">
            <h2>{isLogin ? 'Welcome Back' : 'Create Account'}</h2>
            <p>{isLogin ? 'Sign in to your account to continue' : 'Start your email marketing journey today'}</p>
            
            {isLogin && (
              <div className="login-mode-toggle">
                <button
                  type="button"
                  className={`mode-btn ${loginMode === 'password' ? 'active' : ''}`}
                  onClick={() => {
                    setLoginMode('password');
                    setError(null);
                    setSuccess(null);
                    setOtpSent(false);
                    setOtpEmail('');
                    setOtpCountdown(0);
                    setResendCountdown(0);
                  }}
                >
                  <FiLock />
                  Password
                </button>
                <button
                  type="button"
                  className={`mode-btn ${loginMode === 'otp' ? 'active' : ''}`}
                  onClick={() => {
                    setLoginMode('otp');
                    setError(null);
                    setSuccess(null);
                    setOtpSent(false);
                    setOtpEmail('');
                    setOtpCountdown(0);
                    setResendCountdown(0);
                  }}
                >
                  <FiShield />
                  OTP Login
                </button>
              </div>
            )}
          </div>


          <Formik
            key={`${isLogin}-${loginMode}`}
            initialValues={getInitialValues()}
            validationSchema={getValidationSchema()}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ isSubmitting, touched, errors }) => (
              <Form className="auth-form">
                {!isLogin && (
                  <>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="firstName">First Name</label>
                        <div className="input-wrapper">
                          <FiUser className="input-icon" />
                          <Field
                            type="text"
                            id="firstName"
                            name="firstName"
                            placeholder="Enter your first name"
                            className={touched.firstName && errors.firstName ? 'error' : ''}
                          />
                        </div>
                        <ErrorMessage name="firstName" component="div" className="field-error" />
                      </div>
                      <div className="form-group">
                        <label htmlFor="lastName">Last Name</label>
                        <div className="input-wrapper">
                          <FiUser className="input-icon" />
                          <Field
                            type="text"
                            id="lastName"
                            name="lastName"
                            placeholder="Enter your last name"
                            className={touched.lastName && errors.lastName ? 'error' : ''}
                          />
                        </div>
                        <ErrorMessage name="lastName" component="div" className="field-error" />
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="companyName">Company Name</label>
                      <div className="input-wrapper">
                        <FiUser className="input-icon" />
                        <Field
                          type="text"
                          id="companyName"
                          name="companyName"
                          placeholder="Enter your company name"
                          className={touched.companyName && errors.companyName ? 'error' : ''}
                        />
                      </div>
                      <ErrorMessage name="companyName" component="div" className="field-error" />
                    </div>
                  </>
                )}

                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <div className="input-wrapper">
                    <FiMail className="input-icon" />
                    <Field
                      type="email"
                      id="email"
                      name="email"
                      placeholder="Enter your email address"
                      className={touched.email && errors.email ? 'error' : ''}
                    />
                    {loginMode === 'otp' && otpSent && (
                      <button
                        type="button"
                        className="change-email-inline-btn"
                        onClick={() => {
                          setOtpSent(false);
                          setOtpEmail('');
                          setOtpCountdown(0);
                          setResendCountdown(0);
                          setError(null);
                          setSuccess(null);
                        }}
                      >
                        Change Email
                      </button>
                    )}
                  </div>
                  <ErrorMessage name="email" component="div" className="field-error" />
                </div>

                {loginMode === 'password' && (
                <div className="form-group">
                  <label htmlFor="password">Password</label>
                  <div className="password-input-wrapper">
                    <FiLock className="input-icon" />
                    <Field
                      type={showPassword ? "text" : "password"}
                      id="password"
                      name="password"
                      placeholder="Enter your password"
                      className={`password-input ${touched.password && errors.password ? 'error' : ''}`}
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      className="toggle-password-btn"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex={-1}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                  <ErrorMessage name="password" component="div" className="field-error" />
                </div>
                )}

                {loginMode === 'otp' && otpSent && (
                  <div className="form-group">
                    {/* <div className="otp-email-info">
                    <label htmlFor="otp">Verification Code</label>
                      <div className="email-display">
                        <FiMail className="email-icon" />
                        <span className="email-text">{otpEmail}</span>
                        <button
                          type="button"
                          className="change-email-btn"
                          onClick={() => {
                            setOtpSent(false);
                            setOtpEmail('');
                            setOtpCountdown(0);
                            setResendCountdown(0);
                            setError(null);
                            setSuccess(null);
                          }}
                        >
                          Change Email
                        </button>
                      </div>
                    </div> */}
                    
                    <div className="input-wrapper">
                      <FiShield className="input-icon" />
                      <Field
                        type="text"
                        id="otp"
                        name="otp"
                        placeholder="Enter 6-digit code"
                        className={`otp-input ${touched.otp && errors.otp ? 'error' : ''}`}
                        maxLength="6"
                        style={{ 
                          textAlign: 'center', 
                          letterSpacing: '0.5em', 
                          fontSize: '1.2em',
                          paddingLeft: '45px',
                          paddingRight: '15px',
                          width: '100%'
                        }}
                      />
                    </div>
                    <ErrorMessage name="otp" component="div" className="field-error" />
                    
                    <div className="otp-info">
                      <div className="otp-timer">
                        <FiClock />
                        <span>Code expires in: {Math.floor(otpCountdown / 60)}:{(otpCountdown % 60).toString().padStart(2, '0')}</span>
                      </div>
                      <button
                        type="button"
                        className="resend-btn"
                        onClick={resendOTP}
                        disabled={resendCountdown > 0}
                      >
                        <FiRefreshCw />
                        {resendCountdown > 0 ? `Resend in ${resendCountdown}s` : 'Resend Code'}
                      </button>
                    </div>
                  </div>
                )}

                

                {!isLogin && (
                  <div className="form-group">
                    <label htmlFor="confirmPassword">Confirm Password</label>
                    <div className="password-input-wrapper">
                      <FiLock className="input-icon" />
                      <Field
                        type={showConfirmPassword ? "text" : "password"}
                        id="confirmPassword"
                        name="confirmPassword"
                        placeholder="Confirm your password"
                        className={`password-input ${touched.confirmPassword && errors.confirmPassword ? 'error' : ''}`}
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        className="toggle-password-btn"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        tabIndex={-1}
                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      >
                        {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                      </button>
                    </div>
                    <ErrorMessage name="confirmPassword" component="div" className="field-error" />
                  </div>
                )}

                {isLogin && loginMode === 'password' && (
                  <div className="form-options">
                    <label className="checkbox-wrapper">
                      <input type="checkbox" />
                      <span className="checkmark"></span>
                      Remember me
                    </label>
                    <Link to="/forgotpassword" className="forgot-password">Forgot password?</Link>
                  </div>
                )}

                {loginMode === 'otp' && otpSent && (
                  <div className="otp-actions">
                    <button
                      type="button"
                      className="back-btn"
                      onClick={resetOTPFlow}
                    >
                      ← Back to Email
                    </button>
                  </div>
                )}

                <button 
                  type={loginMode === 'otp' ? "button" : "submit"}
                  className="submit-btn" 
                  disabled={isSubmitting || loading}
                  onClick={async (e) => {
                    
                    if (loginMode === 'otp' && !otpSent) {
                      e.preventDefault();
                      const email = e.target.closest('form').querySelector('input[name="email"]').value;
                      if (email) {
                        await sendOTP(email);
                      } else {
                        showToast.error('Please enter your email address');
                      }
                    } else if (loginMode === 'otp' && otpSent) {
                      e.preventDefault();
                      const email = e.target.closest('form').querySelector('input[name="email"]').value;
                      const otp = e.target.closest('form').querySelector('input[name="otp"]').value;
                      
                      if (!otp) {
                        showToast.error('Please enter the OTP code');
                        return;
                      }
                      
                      setLoading(true);
                      setError(null);
                      
                      try {
                        const response = await AxiosInstance.post(`/user/login`, {
                          EmailAddress: email,
                          otpLogin: true,
                          otp: otp
                        });
                        
                        
                        if (response.data.statusCode === 200) {
                          // Reset logout flag on successful login
                          resetLogoutFlag();
                          
                          // Store token and user data
                          config.setAuthToken(response.data.token);
                          config.setUserId(response.data.user.UserId);
                          
                          // Store role information
                          config.setUserRole(response.data.user.Role || 'member');
                          config.setUserData(response.data.user);
                          
                          // Dispatch custom event to notify App component
                          window.dispatchEvent(new Event('loginSuccess'));
                          
                          // Small delay to ensure state is updated
                          setTimeout(() => {
                            navigate('/');
                          }, 100);
                        } else {
                          showToast.error(response.data.message || 'OTP verification failed');
                        }
                      } catch (error) {
                        console.error('💥 OTP login error:', error);
                        const errorMessage = error.response?.data?.message || 'OTP verification failed';
                        showToast.error(errorMessage);
                      } finally {
                        setLoading(false);
                      }
                    }
                  }}
                >
                  <span>
                    {loading 
                      ? (isLogin 
                          ? (loginMode === 'otp' 
                              ? (otpSent ? 'Verifying...' : 'Sending OTP...') 
                              : 'Signing In...') 
                          : 'Creating Account...') 
                      : (isLogin 
                          ? (loginMode === 'otp' 
                              ? (otpSent ? 'Verify & Sign In' : 'Send OTP') 
                              : 'Sign In') 
                          : 'Create Account')
                    }
                  </span>
                  <FiArrowRight />
                </button>
              </Form>
            )}
          </Formik>

          <div className="auth-divider">
            <span>or</span>
          </div>

          <div className="social-auth">
          <button
  type="button"
  className="social-btn google-btn"
  onClick={handleGoogleLogin}
  disabled={loading}
>
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    style={{ marginRight: "8px" }}
  >
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
  <span>Continue with Google</span>
</button>

            <button 
              type="button"
              className="social-btn github-btn"
              disabled={true}
              style={{ opacity: 0.5, cursor: 'not-allowed' }}
            >
              <FiGithub />
              <span>Continue with GitHub (Coming Soon)</span>
            </button>
          </div>

          <div className="auth-footer">
            <p>
              {isLogin ? "Don't have an account?" : "Already have an account?"}
              <button onClick={toggleMode} className="mode-toggle">
                {isLogin ? 'Sign up' : 'Sign in'}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Auth; 